import { z } from "zod";
import { IsoDateTime } from "../common.ts";

// Authentication and invitation routes of the Explorer API (`iam`, design D4 and D10). Public
// routes are rate limited; the session is iam's own (never a Cognito token).

/** Console session cookie (`HttpOnly; Secure; SameSite=Strict; Path=/`), value never readable by JS. */
export const CONSOLE_SESSION_COOKIE = "__Host-strust_session";
/**
 * Synchronizer token of the session, also set as a JS-readable cookie (`Secure; SameSite=Strict`)
 * so the SPA can send it back after a reload or an SSO redirect.
 */
export const CONSOLE_CSRF_COOKIE = "__Host-strust_csrf";
/**
 * Binds an SSO round trip to the browser that started it (login CSRF): set by `/auth/sso/start`
 * (`HttpOnly; Secure; SameSite=Lax; Path=/`, 10 min), required and cleared by the callback.
 */
export const SSO_BINDING_COOKIE = "__Host-strust_sso";
/**
 * Binds a password login waiting for its TOTP code, its mandatory two-factor set-up or the new
 * password replacing a temporary one to the browser that entered the password (change
 * add-console-account-settings, design D5; change adopt-installation-per-customer, design D13):
 * `HttpOnly; Secure; SameSite=Strict; Path=/`, 3 minutes for the code and the new-password steps
 * and 10 minutes for the set-up; set with a `202` step answer and cleared when the session starts
 * or the step expires.
 */
export const LOGIN_STEP_COOKIE = "__Host-strust_login";
/** Header carrying the synchronizer token on every unsafe method (POST, PUT, PATCH, DELETE). */
export const CSRF_TOKEN_HEADER = "x-csrf-token";

/** 256-bit random value, base64url without padding (session, CSRF and invitation tokens). */
export const OpaqueToken = z
  .string()
  .regex(/^[A-Za-z0-9_-]{43}$/)
  .meta({ id: "OpaqueToken" });

/** `POST /auth/login`. Any email shape is accepted: an unknown one answers `invalid_credentials`. */
export const LoginRequestV1 = z
  .object({
    email: z.string().min(1).max(254),
    password: z.string().min(1).max(256),
  })
  .strict()
  .meta({ id: "LoginRequestV1" });
export type LoginRequestV1 = z.infer<typeof LoginRequestV1>;

// ---- self-service password reset (add-console-account-settings D14, ADR 0015) ------------------

/**
 * `POST /auth/password-reset`: asks for a reset code. Any email shape is accepted and the answer is
 * always the same `202` ({@link PasswordResetAcceptedV1}), whether or not the email belongs to a
 * console user who may reset (no account enumeration).
 */
export const PasswordResetRequestV1 = z
  .object({ email: z.string().min(1).max(254) })
  .strict()
  .meta({ id: "PasswordResetRequestV1" });
export type PasswordResetRequestV1 = z.infer<typeof PasswordResetRequestV1>;

/** The neutral answer of `POST /auth/password-reset`: the same for every email. */
export const PasswordResetAcceptedV1 = z
  .object({ status: z.literal("accepted") })
  .strict()
  .meta({ id: "PasswordResetAcceptedV1" });
export type PasswordResetAcceptedV1 = z.infer<typeof PasswordResetAcceptedV1>;

/** The 6-digit reset code that the identity store emails (never logged or audited). */
export const PasswordResetCode = z
  .string()
  .regex(/^\d{6}$/)
  .meta({ id: "PasswordResetCode" });

/**
 * `POST /auth/password-reset/confirm`: sets a new password with the emailed code. `204` on success;
 * no session starts (the user logs in afterwards). Every refused code answers `invalid_reset_code`.
 */
export const PasswordResetConfirmV1 = z
  .object({
    email: z.string().min(1).max(254),
    code: PasswordResetCode,
    new_password: z.string().min(1).max(256),
  })
  .strict()
  .meta({ id: "PasswordResetConfirmV1" });
export type PasswordResetConfirmV1 = z.infer<typeof PasswordResetConfirmV1>;

/** A started console session (login, invitation acceptance, SSO): cookies are set alongside. */
export const SessionStartedResponseV1 = z
  .object({
    csrf_token: OpaqueToken,
    idle_expires_at: IsoDateTime,
    absolute_expires_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "SessionStartedResponseV1" });
export type SessionStartedResponseV1 = z.infer<typeof SessionStartedResponseV1>;

/**
 * What a right password still needs before a session starts (`202`, design D5): the code of the
 * user's authenticator (`mfa_required`), the mandatory set-up of one (`mfa_setup_required`), or a
 * new password because the password was a temporary one (`new_password_required`, change
 * adopt-installation-per-customer, design D13). The step is bound to the browser by
 * {@link LOGIN_STEP_COOKIE}; no session exists yet.
 */
export const LoginNextStepV1 = z
  .enum(["mfa_required", "mfa_setup_required", "new_password_required"])
  .meta({ id: "LoginNextStepV1" });
export type LoginNextStepV1 = z.infer<typeof LoginNextStepV1>;

export const LoginStepResponseV1 = z
  .object({ next_step: LoginNextStepV1 })
  .strict()
  .meta({ id: "LoginStepResponseV1" });
export type LoginStepResponseV1 = z.infer<typeof LoginStepResponseV1>;

/**
 * `POST /auth/login/new-password`: the password that replaces the temporary one (never stored or
 * logged by iam). The policy (12+ characters, not common or breached) is checked by the server.
 */
export const NewPasswordRequestV1 = z
  .object({ new_password: z.string().min(1).max(256) })
  .strict()
  .meta({ id: "NewPasswordRequestV1" });
export type NewPasswordRequestV1 = z.infer<typeof NewPasswordRequestV1>;

/** A 6-digit TOTP code (RFC 6238, 30-second step) of the user's authenticator app. */
export const TotpCode = z
  .string()
  .regex(/^\d{6}$/)
  .meta({ id: "TotpCode" });

/** `POST /auth/login/mfa` and the confirmation of a two-factor set-up. */
export const MfaCodeRequestV1 = z
  .object({ code: TotpCode })
  .strict()
  .meta({ id: "MfaCodeRequestV1" });
export type MfaCodeRequestV1 = z.infer<typeof MfaCodeRequestV1>;

/**
 * The authenticator to add, answered once with `Cache-Control: no-store` (design D5): the
 * `otpauth://totp/...` URI the console draws as a QR code, and the same base32 secret as text. `iam`
 * never stores or logs the secret; it is never answered again.
 */
export const TwoFactorSetupBeginResponseV1 = z
  .object({
    otpauth_uri: z
      .string()
      .max(1024)
      .regex(/^otpauth:\/\/totp\//),
    secret: z
      .string()
      .min(16)
      .max(128)
      .regex(/^[A-Z2-7]+=*$/),
  })
  .strict()
  .meta({ id: "TwoFactorSetupBeginResponseV1" });
export type TwoFactorSetupBeginResponseV1 = z.infer<typeof TwoFactorSetupBeginResponseV1>;

/** `POST /auth/sso/start`: the email goes in the body, never in the URL. */
export const SsoStartRequestV1 = z
  .object({ email: z.string().min(1).max(254) })
  .strict()
  .meta({ id: "SsoStartRequestV1" });
export type SsoStartRequestV1 = z.infer<typeof SsoStartRequestV1>;

export const SsoStartResponseV1 = z
  .object({ authorization_url: z.url({ protocol: /^https?$/ }) })
  .strict()
  .meta({ id: "SsoStartResponseV1" });
export type SsoStartResponseV1 = z.infer<typeof SsoStartResponseV1>;

/** `GET /auth/sso/start?invitation=<token>`: accepts an invitation through the organization's IdP. */
export const SsoStartQueryV1 = z.object({ invitation: OpaqueToken }).strict();

/** `GET /auth/sso/callback`: the user pool redirect (other parameters are ignored). */
export const SsoCallbackQueryV1 = z.object({
  code: z.string().min(1).max(2048).optional(),
  state: z.string().min(1).max(256).optional(),
  error: z.string().max(256).optional(),
});

/** How the recipient of an invitation signs in: password, or the organization's active IdP. */
export const SignInMethodV1 = z.enum(["password", "sso"]).meta({ id: "SignInMethodV1" });

/** `GET /invitations/{token}`: what the invitation link opens (no role or operator details). */
export const InvitationViewV1 = z
  .object({
    invitation_id: z.uuid(),
    kind: z.enum(["account", "password_setup", "platform_administrator", "two_factor_recovery"]),
    email: z.email(),
    expires_at: IsoDateTime,
    sign_in_method: SignInMethodV1,
  })
  .strict()
  .meta({ id: "InvitationViewV1" });
export type InvitationViewV1 = z.infer<typeof InvitationViewV1>;

/** `POST /invitations/{token}/accept`: the password the recipient chooses (never stored by iam). */
export const AcceptInvitationRequestV1 = z
  .object({ password: z.string().min(1).max(256) })
  .strict()
  .meta({ id: "AcceptInvitationRequestV1" });
export type AcceptInvitationRequestV1 = z.infer<typeof AcceptInvitationRequestV1>;

/** Path parameter of `GET /invitations/{token}` and `POST /invitations/{token}/accept`. */
export const InvitationTokenParamsV1 = z.object({ token: OpaqueToken }).strict();
